import type { TranslationTree } from '../../types';

export const mlRul: TranslationTree = {
  machineLearning: {
    modelPrediction: {
      title: 'Tool life prediction',
      subtitle:
        "A tool's wear estimated from its traceability history, or its remaining life predicted from the machine signals.",
      recent: {
        title: 'Recent predictions',
        runsTitle: 'Recent runs',
        when: 'When',
        tool: 'Tool',
        model: 'Model',
        value: 'Result',
        helpful: 'Helpful prediction',
        notHelpful: 'Wrong prediction',
      },
      views: { wear: 'Tool wear', signals: 'Live signals' },
    },
  },
  mlRul: {
    context: {
      badge: 'Context',
      reset: 'Reset',
      step1Label: 'Machine',
      step2Label: 'Process',
      step3Label: 'Tool',
      step1Placeholder: 'Select a machine',
      step2Placeholder: 'Select a process',
      step3PlaceholderManual: 'Enter a tool ID',
      step3FromProcess: 'From process',
      step3ManualId: 'Manual ID',
      step3Mode: 'How to pick the tool',
      step3ManualHelper: 'Enter the tool identifier as printed on the tool tag.',
      step3NoTools: 'No tools assigned to this process yet.',
      step2NoProcesses: 'No processes are running on this machine.',
      runButton: 'Run inference',
      runButtonLoading: 'Running inference...',
      cancelButton: 'Cancel',
      retryButton: 'Retry',
      step4Label: 'Cutting conditions',
      step4BadgeDefaults: 'defaults',
      step4BadgeFromProcess: 'from process',
      step4BadgeCustom: 'custom',
      step4ResetToDefaults: 'Reset to defaults',
      step4PreFilled: 'pre-filled',
      signalSourceLabel: 'Signal source',
      signalSourcePending: 'Shown once a machine is chosen.',
      signalSourceMissing:
        'This machine has no signal source, so no prediction can run. Link one in Workshop → Machines.',
      runNeedsSignalSource: 'Link a signal source to this machine to run the prediction.',
      params: {
        fz: 'Feed per tooth',
        ap: 'Axial depth of cut',
        ae: 'Radial depth of cut',
        vc: 'Cutting speed',
        nbDents: 'Number of teeth',
        diameterCut: 'Tool diameter',
        units: {
          fz: 'mm/z',
          ap: 'mm',
          ae: 'mm',
          vc: 'm/min',
          nbDents: '—',
          diameterCut: 'mm',
        },
      },
    },
    tool: {
      unknownReference: 'Unknown reference',
      lastUsed: 'Last used {{date}}',
      lastUsedNever: 'Never used',
      statusInUse: 'In use',
      statusIdle: 'Idle',
      statusScrapped: 'Scrapped',
    },
    result: {
      badgeIdle: 'Waiting for inference',
      badgeRunning: 'Running inference',
      badgeCompleted: 'Inference completed',
      badgeFailed: 'Inference failed',
      idleTitle: 'Pick a machine, a process, and a tool',
      idleDescription: 'The remaining useful life will appear here once you run an inference.',
      runningOnTool: 'Running inference on {{toolId}}',
      rulLabel: 'Remaining useful life',
      secondsUnit: 'seconds',
      humanReadable: 'approximately {{hours}} hours {{minutes}} minutes of remaining cutting time',
      humanReadableSeconds: 'approximately {{seconds}} seconds of remaining cutting time',
      humanReadableDays: 'approximately {{days}} days {{hours}} hours of remaining cutting time',
      contextChipMachine: 'Machine',
      contextChipProcess: 'Process',
      contextChipTool: 'Tool',
      contextChipJob: 'Job',
      errorTitle: 'Inference failed',
      window: {
        part: 'Part {{part}}',
        passes: { one: '{{count}} pass', other: '{{count}} passes' },
        toolVolume: 'Tool volume {{volume}} cm³',
      },
    },
    signal: {
      badge: 'Inference signal',
      helper: 'Last {{passes}} passes · as read by the model',
      helperPart: 'Last {{passes}} passes of part {{part}} · as read by the model',
      openLink: 'Open in Signal Visualization →',
      errorTitle: 'Signal unavailable',
      errorRetry: 'Retry',
    },
    errors: {
      missingSignalSource: 'This machine has no signal source. Link one in Workshop → Machines.',
      noSignalData:
        "The signals database holds no data for this machine's signal source in the last 90 days.",
      noMachiningPass:
        'No machining pass the model knows. The last {{seconds}} s of part {{part}} hold no pass of the lab block (|X|, |Y| ≤ 13 mm, Z ≤ 0). The live model was trained on those passes only.',
      noMachiningPassGeneric:
        "No machining pass the model knows in the machine's newest part: no pass of the lab block (|X|, |Y| ≤ 13 mm, Z ≤ 0). The live model was trained on those passes only.",
    },
  },
  mlWear: {
    context: {
      tool: 'Tool',
      toolPlaceholder: 'Select a tool',
      toolTraces: {
        one: '{{count}} trace · last on {{date}}',
        other: '{{count}} traces · last on {{date}}',
      },
      toolNoTrace: 'No trace for this tool.',
      toolsLoadError: 'The tool list could not be loaded.',
      diameter: 'Cutting diameter',
      diameterField: 'Cutting Ø',
      required: 'required',
      diameterSource: 'Diameter source',
      modeAdaptcut: 'Adaptcut tool',
      modeManual: 'Typed',
      adaptcutPlaceholder: 'Select an Adaptcut tool',
      adaptcutSearch: 'Search a name or a reference',
      adaptcutList: 'Adaptcut tools',
      adaptcutNoMatch: 'No Adaptcut tool matches.',
      adaptcutSameReference: 'same reference',
      adaptcutTag: 'Adaptcut',
      adaptcutLine: 'Cutting Ø {{cut}} · {{teeth}} teeth · shank Ø {{shank}}',
      adaptcutNoDiameter: 'This Adaptcut tool has no cutting diameter. Type it below.',
      adaptcutError: 'The Adaptcut tool list could not be loaded.',
      retry: 'Retry',
      diameterHelpUsed: 'Used for the whole history and for the next operation.',
      diameterHelpRequired: "Type the tool's cutting diameter.",
      diameterHelpNoMatch:
        'No Adaptcut tool has the reference {{reference}}: type the cutting diameter or pick another Adaptcut tool.',
      diameterHelpAdaptcutDown: 'Adaptcut is unreachable: type the cutting diameter.',
      diameterHelpPick: 'Pick an Adaptcut tool, or type the diameter.',
      next: 'Next operation',
      optional: 'optional',
      fromTrace: 'last trace',
      edited: 'edited',
      volumePerPiece: 'Volume per piece',
      plannedPieces: 'Planned pieces',
      nextHelpFromTrace: 'Volume per piece = volume ÷ pieces of the last trace.',
      nextHelpIgnored: 'ap and vc do not change the prediction.',
      backToTrace: 'Use the last trace again',
      save: 'Save the prediction',
      saving: 'Saving…',
      saved: 'Prediction saved',
      saveError: 'The prediction could not be saved.',
      needsDiameter: 'Enter the cutting diameter to compute the wear.',
      units: { mm: 'mm', mmPerTooth: 'mm/tooth', cm3: 'cm³', pieces: 'pcs' },
    },
    result: {
      idle: 'Select a tool to estimate its wear.',
      needsDiameter: 'Enter the cutting diameter (step 1) to compute the wear.',
      loading: 'Computing the wear…',
      modelUnavailableTitle: 'The wear model is not deployed on this server.',
      modelUnavailable:
        "An administrator has to register wear_factor in the model registry. The tool's history stays visible in Traceability.",
      error: 'The wear could not be computed.',
      reportedWorn: 'Tool reported worn in its trace of {{date}}.',
      reportedBroken: 'Tool reported broken in its trace of {{date}}.',
      reportedDetail:
        'The model puts its wear at {{wear}}, with a {{pWorn}} probability of being worn.',
      outOfRangeTitle: "Outside the model's fitted range: the result is an extrapolation.",
      outOfRange: {
        material: 'material {{values}} (fitted on {{fitted}})',
        ap: 'ap {{values}} mm (fitted on {{fitted}} mm)',
        ae: 'ae {{values}} mm (fitted on {{fitted}} mm)',
        diameter: 'diameter {{values}} mm (fitted on {{fitted}} mm)',
        hm: 'chip thickness hm {{values}} µm (fitted on {{fitted}} µm)',
      },
      stateTitle: 'Tool state',
      tracesCounted: {
        one: '{{count}} trace counted',
        other: '{{count}} traces counted',
      },
      tracesSkipped: {
        one: '{{count}} skipped',
        other: '{{count}} skipped',
      },
      wear: 'Estimated wear',
      wearOfLife: 'of the predicted life',
      pWorn: 'Probability of being worn already',
      milled: 'Already milled',
      milledPieces: {
        one: '{{count}} piece',
        other: '{{count}} pieces',
      },
      gaugeLabel: 'Wear {{wear}} of the predicted life',
      gaugePrudent: 'prudent {{value}}',
      gaugeEnd: 'predicted end of life',
      capacityTitle: 'Still millable',
      capacitySettings: 'fz {{fz}} · ae {{ae}} · Ø {{d}} → hm {{hm}} µm',
      outOfRangeTag: 'outside the range',
      overestimatedTag: 'overestimated',
      lowerBoundTag: 'minimum',
      missingCell: 'missing',
      incomplete: {
        titleSome: {
          one: 'Wear underestimated: {{count}} trace of {{total}} is not counted',
          other: 'Wear underestimated: {{count}} traces of {{total}} are not counted',
        },
        titleNone: {
          one: 'No usable trace: the only trace is not counted',
          other: 'No usable trace: none of the {{count}} traces is counted',
        },
        consequence:
          'The wear and the probability shown are minimums; what the tool can still mill is overestimated.',
        missing: { one: '{{fields}} missing', other: '{{fields}} missing' },
        link: "Complete the traces on the tool's page →",
      },
      capacityEmpty:
        'Enter fz and ae of the next operation to estimate what the tool can still mill.',
      prudent: 'Prudent',
      prudentHelp: '1 tool in 10 would be worn before.',
      median: 'Median',
      medianHelp: 'Half of the tools would be worn before.',
      piecesUnit: 'pieces',
      needsVolumePerPiece: 'Enter the volume per piece to count the pieces.',
      planned:
        'After the {{pieces}} planned pieces ({{volume}} cm³): wear {{wear}}, probability of being worn {{pWorn}}.',
      plannedRisky: 'Risk above 1 in 10.',
      plannedSafe: 'Within the prudent margin.',
      historyTitle: 'Tool history',
      historyHelp: 'Each trace adds its volume divided by the tool life at its settings.',
      historyEmpty: 'No trace: the tool counts as new.',
      columns: {
        date: 'Date',
        operation: 'Operation',
        machine: 'Machine',
        material: 'Material',
        ap: 'ap',
        ae: 'ae',
        fz: 'fz',
        hm: 'hm µm',
        pieces: 'Pieces',
        volume: 'Volume cm³',
        wearAdded: 'Wear +',
        cumulative: 'Cumulative',
        state: 'State',
      },
      skipped: 'not counted: {{reason}}',
      skippedReasons: { missing_volume: 'volume missing', missing_settings: 'ae or fz missing' },
      provenance: 'Model {{name}} {{version}} from the registry',
      provenanceFitted: 'fitted on {{tools}} tools in {{materials}}, Ø {{diameter}} mm',
      provenanceError: 'life error {{error}} min in cross-validation',
    },
    recent: {
      left: 'Still millable (median)',
      settings: 'Settings',
      leftPieces: '{{pieces}} pieces · {{volume}} cm³',
      leftVolume: '{{volume}} cm³',
    },
  },
};
